import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

/**
 * Quantity-break tiles. A tile only sets the quantity in the product form. The discount itself comes from
 * Shopify automatic discounts with the same thresholds (theme settings sk_bundle_tier2_pct / sk_bundle_tier3_pct).
 */
class SkQuantityBreaks extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.addEventListener('change', this.#onChange);
    this.#scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#scope?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const selector = this.#scope?.querySelector('product-form-component quantity-selector-component');
    if (selector && typeof selector.setValue === 'function') {
      selector.setValue(input.value);
      // setValue only writes the input. Refresh the -/+ buttons and broadcast the change (the sticky bar listens).
      // The broadcast reaches #onQuantityUpdate, which re-checks the same radio: idempotent, no loop.
      selector.updateButtonStates?.();
      selector.onQuantityChange?.();
      return;
    }
    const quantityInput = this.#scope?.querySelector('product-form-component input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = input.value;
  };

  /** Keeps the highlighted tile in sync when the shopper uses the +/- quantity selector. */
  #onQuantityUpdate = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('product-form-component')) return;
    const quantity = String(event.detail?.quantity ?? '');
    for (const radio of this.querySelectorAll('.sk-breaks__input')) {
      if (radio instanceof HTMLInputElement) radio.checked = radio.value === quantity;
    }
  };

  /** Variant changed: copy the new tile prices from the re-rendered section HTML. */
  #onProductSelect = (event) => {
    const prices = this.querySelectorAll('.sk-breaks__price, .sk-breaks__was');
    prices.forEach((el) => el.classList.add('is-updating'));
    Promise.resolve(event.promise)
      .then((result) => {
        const fresh = result?.detail?.html?.querySelector(
          `sk-quantity-breaks[data-block-id="${this.dataset.blockId}"]`
        );
        if (!fresh) return;
        const next = fresh.querySelectorAll('.sk-breaks__price, .sk-breaks__was');
        prices.forEach((el, index) => {
          if (next[index]) el.textContent = next[index].textContent;
        });
      })
      .catch(() => {})
      .finally(() => prices.forEach((el) => el.classList.remove('is-updating')));
  };
}

if (!customElements.get('sk-quantity-breaks')) {
  customElements.define('sk-quantity-breaks', SkQuantityBreaks);
}
